import { User, UserGroups } from '@ravago/shared/authentication';
import { FeatureToggle, UserToggles } from '@ravago/shared/page-data/models/config';
import {
   CallOffOrderColor,
   ProductColor,
   DynamicProductBlockColor,
   DynamicProductBlockDefaultColor,
   ProductDetail,
   QuotedColor
} from '@ravago/shared/page-data/models/elements';

export class DynamicProductBlockUtils {
   static readonly getUserToggles = (user: User | undefined, toggles: FeatureToggle[], privateFeatureToggles: FeatureToggle[]) => {
      const userToggles: UserToggles = {
         hasGuestToggle: false,
         hasPendingToggle: false,
         hasAnonymousToggle: false,
         hasVerifiedToggle: false
      };

      if (!user) {
         userToggles.hasAnonymousToggle = toggles.find((feature) => feature.name === 'public-request-for-quotation-anonymous-user')?.allowed ?? false;
         return userToggles;
      }

      const userGroups = user.groups ?? [];

      if (userGroups.includes(UserGroups.digitalPlatformGuest)) {
         userToggles.hasGuestToggle = privateFeatureToggles.some((toggle) => toggle.name === 'request-for-quotation-guest-user' && toggle.allowed);
         return userToggles;
      }

      if (userGroups.includes(UserGroups.digitalPlatformPendingVerification)) {
         userToggles.hasPendingToggle = privateFeatureToggles.some(
            (toggle) => toggle.name === 'request-for-quotation-pending-user' && toggle.allowed
         );
         return userToggles;
      }

      userToggles.hasVerifiedToggle = privateFeatureToggles.some((toggle) => toggle.name === 'request-for-quotation-verified-user' && toggle.allowed);

      return userToggles;
   };

   static readonly hasUserToggle = (user: User | undefined, userToggles: UserToggles) => {
      if (!user) {
         return userToggles.hasAnonymousToggle;
      }

      if (user.groups?.includes(UserGroups.digitalPlatformGuest)) {
         return userToggles.hasGuestToggle;
      }

      if (user.groups?.includes(UserGroups.digitalPlatformPendingVerification)) {
         return userToggles.hasPendingToggle;
      }

      return userToggles.hasVerifiedToggle;
   };

   static readonly getDefaultColorValue = (
      defaults: DynamicProductBlockDefaultColor | undefined,
      product: ProductDetail,
      callOffOrderColors: CallOffOrderColor[],
      quotedColors: QuotedColor[],
      quotedColorsEnabled: boolean
   ): DynamicProductBlockColor | undefined => {
      if (defaults?.defaultColorName) {
         const defaultColor =
            (defaults.from === 'call-off' && callOffOrderColors.find((color) => color.color.name === defaults.defaultColorName)) ||
            (defaults.from === 'quote' && quotedColors.find((color) => color.color.name.includes(defaults.defaultColorName ?? ''))) ||
            product.colors.find((color) => color.name === defaults.defaultColorName);

         if (defaultColor) {
            return DynamicProductBlockUtils.createColorFromDefaultColor(defaultColor, defaults, quotedColorsEnabled);
         }
      }

      if (callOffOrderColors.length > 0) {
         return DynamicProductBlockUtils.createColorFromCallOffOrder(callOffOrderColors[0]);
      }

      if (quotedColors.length > 0) {
         const earliestQuotedColor = DynamicProductBlockUtils.findEarliestQuotedColor(quotedColors);
         return DynamicProductBlockUtils.createColorFromQuotedColor(earliestQuotedColor);
      }

      if (product.colors.length === 1) {
         return DynamicProductBlockUtils.createColorFromProductColor(product.colors[0]);
      }

      return undefined;
   };

   static readonly getProductSlugWithParams = (slug: string, color: string, colorCode: string, from?: string): string => {
      const params = new URLSearchParams();
      params.set('color', color);

      if (colorCode) {
         params.set('producerColorCode', colorCode);
      }

      if (from) {
         params.set('from', from);
      }

      return `${slug}?${params.toString()}`;
   };

   private static readonly createColorFromDefaultColor = (
      defaultColor: CallOffOrderColor | QuotedColor | ProductColor,
      defaults: DynamicProductBlockDefaultColor,
      quotedColorsEnabled: boolean
   ): DynamicProductBlockColor => {
      const isCallOffOrQuoted = 'color' in defaultColor;
      const defaultColorCode = isCallOffOrQuoted ? defaultColor.color.producerColorCode : defaults.defaultColorCode;

      return {
         colorId: isCallOffOrQuoted ? defaultColor.color.id : defaultColor.id,
         colorName: isCallOffOrQuoted ? defaultColor.color.name : defaultColor.name,
         producerColorCode: defaultColorCode,
         desiredColor: isCallOffOrQuoted ? undefined : defaults.defaultDesiredColor,
         callOffOrderId: 'recommendedCallOff' in defaultColor ? defaultColor.recommendedCallOff.id : undefined,
         isQuotedColor: quotedColorsEnabled ? defaults.from === 'quote' : false,
         quantity: undefined
      };
   };

   private static readonly createColorFromCallOffOrder = (callOffOrderColor: CallOffOrderColor): DynamicProductBlockColor => ({
      colorId: callOffOrderColor.color.id,
      colorName: callOffOrderColor.color.name,
      producerColorCode: callOffOrderColor.color.producerColorCode,
      desiredColor: undefined,
      callOffOrderId: callOffOrderColor.recommendedCallOff.id,
      isQuotedColor: false,
      quantity: undefined
   });

   private static readonly findEarliestQuotedColor = (quotedColors: QuotedColor[]): QuotedColor =>
      quotedColors.reduce((earliest, current) => {
         const earliestDate = new Date(earliest.quotation.effective.until);
         const currentDate = new Date(current.quotation.effective.until);
         return currentDate < earliestDate ? current : earliest;
      }, quotedColors[0]);

   private static readonly createColorFromQuotedColor = (quotedColor: QuotedColor): DynamicProductBlockColor => ({
      colorId: quotedColor.color.id,
      colorName: quotedColor.color.name,
      producerColorCode: quotedColor.color.producerColorCode,
      desiredColor: undefined,
      callOffOrderId: undefined,
      isQuotedColor: true,
      quantity: undefined
   });

   private static readonly createColorFromProductColor = (productColor: ProductColor): DynamicProductBlockColor => ({
      colorId: productColor.id,
      colorName: productColor.name,
      producerColorCode: undefined,
      desiredColor: undefined,
      callOffOrderId: undefined,
      isQuotedColor: false,
      quantity: undefined
   });
}
